import { useProgramClass } from '@/hooks/useProgramClass';
import { Workshop } from '@/models/Workshop';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { WorkshopForm, workshopSchema } from '@/pages/Contents/LMS/Classes/WorkshopClassForm/formConfig';
import { ApplicationState } from '@/store';
import { isBefore } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ValidationError } from 'yup';
interface Props {
    onChange: (isOnline: boolean | undefined, address: string) => void;
}

const WorkshopType: React.FC<Props> = ({ onChange }: Props) => {
    const { courseId: courseIdString, classId: classIdString } = useParams<{ courseId: string; classId: string }>();

    const courseId = Number(courseIdString);
    const classId = Number(classIdString);

    const strings = useSelector((state: ApplicationState) => state.locale.data);

    const [isOnline, setIsOnline] = useState<boolean | undefined>(undefined);
    const [address, setAddress] = useState<string>('');
    const [firstMeeting, setFirstMeeting] = useState<WorkshopMeeting>();
    const [isInvalidUrl, setIsInvalidUrl] = useState<boolean>(false);

    const isClassStarted: boolean = Boolean(firstMeeting?.startDate && isBefore(firstMeeting.startDate, new Date()));

    const { programClass } = useProgramClass({
        programClassId: classId,
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const workshopProgramClass: Workshop | undefined = programClass?.workshops && programClass.workshops[0];

    const workshop: Workshop | undefined = !!courseId
        ? programClass?.workshops?.find((workshop: Workshop) => workshop.course.id === courseId)
        : workshopProgramClass;

    const validateAddress = (): void => {
        workshopSchema
            .validateAt('address', { isOnline, address } as WorkshopForm)
            .then(() => setIsInvalidUrl(false))
            .catch((error: ValidationError) => setIsInvalidUrl(error.path === 'address' && error.type === 'url'));
    };

    useEffect(() => {
        if (workshop) {
            setIsOnline(workshop.isOnline);
            setAddress(workshop.address);
            setFirstMeeting(workshop.meetings[0]);
        }
    }, [workshop]);

    useEffect(() => {
        validateAddress();
        onChange(isOnline, address);
    }, [isOnline, address]);

    return (
        <div className="container-workshop-type">
            <p>{strings.WORKSHOP_TYPE}</p>
            <div className="container-workshop-type__buttons">
                <button className={`btn btn-primary btn-sm ${isOnline === true ? 'selected' : ''}`} disabled={isClassStarted} type="button">
                    <input
                        className="radio"
                        disabled={isClassStarted}
                        id="on-line"
                        onClick={() => {
                            setAddress((addressState: string) => (isOnline === true ? addressState : ''));
                            setIsOnline(true);
                        }}
                        type="radio"
                    />
                    <label htmlFor="on-line">{strings.WORKSHOP_TYPE_ONLINE}</label>
                </button>
                <button className={`btn btn-primary btn-sm ${isOnline === false ? 'selected' : ''}`} disabled={isClassStarted} type="button">
                    <input
                        className="radio"
                        disabled={isClassStarted}
                        id="in-person"
                        onClick={() => {
                            setAddress((addressState: string) => (isOnline === false ? addressState : ''));
                            setIsOnline(false);
                        }}
                        type="radio"
                    />
                    <label htmlFor="in-person">{strings.WORKSHOP_TYPE_IN_PERSON}</label>
                </button>
            </div>

            {isOnline !== undefined && (
                <>
                    <span className="input-section-info">
                        {isOnline ? strings.WORKSHOP_URL : strings.WORKSHOP_ADDRESS}
                        <span>*</span>
                    </span>
                    <input
                        className={`form-control ${isInvalidUrl ? 'invalid-input' : ''}`}
                        disabled={isClassStarted}
                        maxLength={255}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)}
                        placeholder={isOnline ? strings.WORKSHOP_URL_PLACEHOLDER : strings.WORKSHOP_ADDRESS_PLACEHOLDER}
                        required
                        type="text"
                        value={address}
                    />
                    <span className="input-section-info invalid-field mt-0">{isInvalidUrl && strings.INSERT_INVALID_LINK}</span>
                </>
            )}
        </div>
    );
};

export default WorkshopType;
